import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    ActivityIndicator,
    Button,
    Grid,
    Icon,
    Snackbar,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import ClassNames from 'classnames';
import {
    isEmpty,
    isFinite,
    isFunction,
    isNil,
    isString,
    noop,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    Currency,
    UIMode,
} from '../../../global/models';
import {
    getCampaignCommitmentsByCampaignForHousehold,
    getCampaignDetails,
    postCampaignCommitment,
    putCampaignCommitment,
} from '../api/campaign.service';
import LoadError from '../shared/components/loadError';
import {
    CAMPUS_ID_BERLIN,
    CAMPUS_ID_HONG_KONG,
    CAMPUS_ID_SANTA_ROSA,
    CAMPUS_ID_VANCOUVER,
    DEFAULT_LOCALE,
    INTL_CAMPUS_IDS,
    ROCK_TO_HC_CAMPUS_ID_MAP,
} from '../shared/js/constants';
import {
    Campaign,
    CampaignCommitment as CampaignCommitmentModel,
    EditableCurrencyAmount,
    OnlinePaymentChannel,
} from '../shared/models';
import CommitmentTitle from './commitmentTitle';
import { BEM_BLOCK_NAME } from './constants';
import InternationalGivingInstructions from './intlGivingInstructions';
import SetCommitment from './setCommitment';
import ViewCommitment from './viewCommitment';

type PropTypes = {
    accessToken: string | null;
    campaign?: Campaign | null;
    campaignId: number | null;
    /**
     * Should be interpretted according to the value of **`useRockApis`**.<br />
     * * If **`useRockApis`** is **`true`** then should be a **Rock** Campus ID.
     * * If **`useRockApis`** is **`false`** (or omitted) then should be a **Healthy Church** Campus ID.
     */
    campusId?: number;
    channel?: OnlinePaymentChannel;
    commitment?: CampaignCommitmentModel | null;
    hasCheckedForCommitment?: boolean;
    /** Hides the "Give to Dream Now" heading above the giving buttons; the blurb and buttons still render */
    hideWaysToGiveTitle?: boolean;
    giveCryptoButtonText?: string;
    giveNowButtonText?: string;
    giveStockButtonText?: string;
    isFetchingCommitmentExternally?: boolean;
    locale?: string;
    onCommitmentSaved?: ((newCommitment: CampaignCommitmentModel) => void) | string;
    onGiveNowClick: (() => void) | string;
    onGiveViaCryptoClick?: (() => void) | string;
    onGiveViaStocksClick?: (() => void) | string;
    onSignInClick: (() => void) | string;
    personId: number | null;
    useRockApis?: boolean;
};

const defaultProps: Pick<
PropTypes,
'campaign' |
'campusId' |
'channel' |
'commitment' |
'giveCryptoButtonText' |
'giveNowButtonText' |
'giveStockButtonText' |
'hasCheckedForCommitment' |
'hideWaysToGiveTitle' |
'isFetchingCommitmentExternally' |
'locale' |
'onCommitmentSaved' |
'useRockApis'
> = {
    campaign: null,
    campusId: undefined,
    channel: OnlinePaymentChannel.Web,
    commitment: null,
    giveCryptoButtonText: 'Crypto',
    giveNowButtonText: 'Give Now',
    giveStockButtonText: 'Stocks',
    hasCheckedForCommitment: false,
    hideWaysToGiveTitle: false,
    locale: undefined,
    onCommitmentSaved: noop,
    useRockApis: false,
};

const useStyles = makeStyles(({
    palette,
    spacing,
    typography,
}) => ({
    root: {
        backgroundColor: palette.background.light,
        border: `solid 1px ${palette.border.primary}`,
        borderRadius: 8,
        margin: '0 auto',
        padding: spacing(2),
        width: '100%',
    },
    amountInput: {
        '& input': {
            paddingLeft: `${spacing(2)}px !important`,
        },
        '& .input-icon-custom': {
            left: spacing(1),
            marginTop: '-11.5px !important',
            width: spacing(1),
        },
    },
    buttonContainer: {
        display: 'flex',
        marginTop: spacing(1),
    },
    cryptoButton: {
        flexBasis: '33%',
    },
    loadingIndicatorContainer: {
        textAlign: 'center',
    },
    subtitle: {
        fontWeight: typography.fontWeightMedium,
    },
    title: {
        fontWeight: typography.fontWeightBold,
    },
    signInButton: {
        marginTop: spacing(1),
    },
    signInMessage: {
        fontSize: typography.pxToRem(16),
        lineHeight: 1.5,
    },
    signInMessageContainer: {
        display: 'grid',
        gap: 11,
    },
    giveNowContainer: {
        marginTop: spacing(3),
    },
    giveNowTitle: {
        fontWeight: typography.fontWeightBold,
    },
    giveNowCopy: {
        marginTop: spacing(1),
    },
    giveNowButton: {
        flexBasis: '33%',
    },
    stocksButton: {
        flexBasis: '33%',
    },
}));

const CLASS_NAME_BUTTON_CONTAINER: string = `${BEM_BLOCK_NAME}--button_container`;
const CLASS_NAME_GIVE_NOW_BUTTON: string = `${BEM_BLOCK_NAME}--give_now_button`;
const CLASS_NAME_GIVE_NOW_CONTAINER: string = `${BEM_BLOCK_NAME}--give_now_container`;
const CLASS_NAME_GIVE_NOW_COPY: string = `${BEM_BLOCK_NAME}--give_now_copy`;
const CLASS_NAME_GIVE_NOW_TITLE: string = `${BEM_BLOCK_NAME}--give_now_title`;
const CLASS_NAME_GIVE_WITH_CRYPTO_BUTTON: string = `${BEM_BLOCK_NAME}--crypto_button`;
const CLASS_NAME_GIVE_WITH_STOCKS_BUTTON: string = `${BEM_BLOCK_NAME}--stocks_button`;
const CLASS_NAME_LOADING_INDICATOR_CONTAINER: string = `${BEM_BLOCK_NAME}--loading_indicator`;
const CLASS_NAME_SIGN_IN_BUTTON: string = `${BEM_BLOCK_NAME}--sign_in_button`;
const CLASS_NAME_SIGN_IN_MESSAGE: string = `${BEM_BLOCK_NAME}--sign_in_message`;
const CLASS_NAME_SIGN_IN_MESSAGE_CONTAINER: string = `${BEM_BLOCK_NAME}--sign_in_message_container`;

/**
 * Collection of IDs of international campuses
 * whose special intructions also suppress
 * the regular title and blurb of the 'Give Now' section.
 */
const INTL_CAMPUS_IDS_SUPPRESS_GIVE_NOW_TITLE = [
    CAMPUS_ID_HONG_KONG,
    CAMPUS_ID_VANCOUVER,
];

function CampaignCommitment({
    accessToken,
    campaign: prefetchedCampaign,
    campaignId,
    campusId,
    channel,
    commitment: prefetchedCommitment,
    // TODO/FIXME: Below props are not currently being used, because now button text has translations via i18next.
    //             Do we still need/want to support customized text for en-US locale?
    giveCryptoButtonText, // eslint-disable-line @typescript-eslint/no-unused-vars
    giveNowButtonText, // eslint-disable-line @typescript-eslint/no-unused-vars
    giveStockButtonText, // eslint-disable-line @typescript-eslint/no-unused-vars
    hasCheckedForCommitment,
    hideWaysToGiveTitle,
    isFetchingCommitmentExternally,
    locale,
    onCommitmentSaved: onCommitmentSavedProp,
    onGiveNowClick: onGiveNowClickProp,
    onGiveViaCryptoClick: onGiveViaCryptoClickProp,
    onGiveViaStocksClick: onGiveViaStocksClickProp,
    onSignInClick: onSignInClickProp,
    personId,
    useRockApis,
}: PropTypes) {
    const classes = useStyles();

    const { i18n, t } = useTranslation();

    const [campaign, setCampaign] = useState<Campaign | null>(prefetchedCampaign ?? null);
    const [commitment, setCommitment] = useState<CampaignCommitmentModel | null>(prefetchedCommitment ?? null); // eslint-disable-line max-len
    const [existingGoal, setExistingGoal] = useState<EditableCurrencyAmount | null>(null);
    const [hasError, setHasError] = useState<boolean>(false);
    const [isDirtyInEditMode, setIsDirtyInEditMode] = useState<boolean>(false);
    const [isFetchingCampaign, setIsFetchingCampaign] = useState<boolean>(false);
    const [isFetchingCommitment, setIsFetchingCommitment] = useState<boolean>(false);
    const [isSaveSuccessful, setIsSaveSuccessful] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
    const [saveMessage, setSaveMessage] = useState<string>('Goal Set');
    const [uiMode, setUiMode] = useState<UIMode>(UIMode.Add);

    const safeCampusId = useMemo(() => {
        if (isNil(campusId)) {
            return campusId;
        }

        if (!useRockApis) {
            return campusId;
        }

        return ROCK_TO_HC_CAMPUS_ID_MAP[campusId];
    }, [
        campusId,
        useRockApis,
    ]);

    const isUserLoggedIn: boolean = !isEmpty(accessToken);
    const hasCampaignId: boolean = !isNil(campaignId);

    const currency = useMemo<Currency>(() => {
        if (isNil(safeCampusId)) {
            return Currency.USD;
        }

        // TODO: Maybe in future we can make this data-driven as part of Campus model.
        // Right now, we're on the clock, so here comes the poor man's version w/ some hard-coding.
        switch (safeCampusId) {
            case CAMPUS_ID_BERLIN:
                return Currency.EUR;
            case CAMPUS_ID_HONG_KONG:
                return Currency.HKD;
            case CAMPUS_ID_SANTA_ROSA:
                return Currency.PHP;
            case CAMPUS_ID_VANCOUVER:
                return Currency.CAD;
            // NOTE: Campus ID 20 Buenos Aires Argentina conducts its giving in US Dollars
            //       and not local currency (ARS) for economic reasons.
            default:
                return Currency.USD;
        }
    }, [safeCampusId]);

    const hasSpecialGivingInstructions = INTL_CAMPUS_IDS.includes(safeCampusId);
    const suppressGiveNowTitleAndBlurb = INTL_CAMPUS_IDS_SUPPRESS_GIVE_NOW_TITLE.includes(safeCampusId); // eslint-disable-line max-len
    const localizedCampaignName = t('common.infoContainer.campaignName'); // TODO/FIXME: Currently translations are hard-coded for Dream Now Campaign.

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('Healthy Church Giving Campaign Commitment Widget: Using Rock APIs:', useRockApis);
    }, [useRockApis]);

    // Currency symbols, amount formatting and the saved `culture` still need a concrete locale.
    // Prefer an explicit `locale`, then the language i18next actually resolved to, so this widget's
    // numbers match its own labels when another embed selected the language.  `resolvedLanguage` and
    // not `language`: a request for a locale we don't translate (e.g. `fr`) leaves `language` as `fr`
    // while the labels fall back to English, and the numbers need to follow the labels, not the ask.
    // i18next's English is the bare `en`, while `DEFAULT_LOCALE` is the canonical code the API and
    // `Intl` already receive today, so English stays on the default rather than changing the wire value.
    const liveLanguage = i18n?.resolvedLanguage;
    const formattingLocale = (
        !isEmpty(locale) && locale
    ) || (
        !isEmpty(liveLanguage) && !liveLanguage.startsWith('en') && liveLanguage
    ) || DEFAULT_LOCALE;

    // See if we need to change the locale.
    // i18next is a singleton shared with the other giving widgets on the page, so an absent `locale`
    // must leave the current language alone rather than forcing everything back to English.
    useEffect(() => {
        if (!isEmpty(locale) &&
            !isNil(i18n) &&
            isFunction(i18n.changeLanguage) &&
            locale !== i18n.language) {
            // eslint-disable-next-line no-console
            console.log('Healthy Church Giving Campaign Commitment Widget: i18n - Changing language to:', locale);
            i18n.changeLanguage(locale);
        }
    }, [
        i18n,
        locale,
    ]);

    useEffect(() => {
        if (hasCampaignId && isNil(campaign)) {
            setIsFetchingCampaign(true);

            getCampaignDetails(campaignId)
                .then((campaignDetails) => {
                    setCampaign(campaignDetails);
                })
                .catch(() => {
                    setHasError(true);
                })
                .finally(() => {
                    setIsFetchingCampaign(false);
                });
        }
    }, [
        campaign,
        campaignId,
        hasCampaignId,
    ]);

    const setExistingCommitmentState = useCallback((
        existingCommitment: CampaignCommitmentModel,
    ) => {
        const editableCommitment = {
            ...existingCommitment,
            goal: {
                ...existingCommitment.goal,
                amountAsString: new Intl.NumberFormat('en-US', { // _don't_ use locale here; always format `amountAsString` in en-US locale so it can be loaded for editing
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    useGrouping: false,
                }).format(existingCommitment.goal.amount),
            },
        };

        setCommitment(editableCommitment);
        setUiMode(UIMode.View);
    }, []);

    useEffect(() => {
        if (hasCampaignId &&
            isUserLoggedIn) {
            if (!hasCheckedForCommitment) {
                setIsFetchingCommitment(true);

                getCampaignCommitmentsByCampaignForHousehold(
                    accessToken,
                    campaignId,
                )
                    .then((hhCommitments) => {
                        const existingCommitment = hhCommitments?.myCommitment ?? hhCommitments?.mySpousesCommitment; // eslint-disable-line max-len

                        const hasExistingCommitment = !isNil(existingCommitment);

                        if (hasExistingCommitment) {
                            setExistingCommitmentState(existingCommitment);
                        }
                    })
                    .catch(() => {
                        setHasError(true);
                    })
                    .finally(() => {
                        setIsFetchingCommitment(false);
                    });
            } else if (!isNil(prefetchedCommitment)) {
                setExistingCommitmentState(prefetchedCommitment);
            }
        }
    }, [
        accessToken,
        campaignId,
        hasCampaignId,
        hasCheckedForCommitment,
        isUserLoggedIn,
        prefetchedCommitment,
        setExistingCommitmentState,
        useRockApis,
    ]);

    useEffect(() => {
        if (!isNil(isFetchingCommitmentExternally)) {
            setIsFetchingCommitment(isFetchingCommitmentExternally);
        }
    }, [
        isFetchingCommitmentExternally,
    ]);

    const onCancelEditMode = useCallback(() => {
        const restoredCommitment: CampaignCommitmentModel = {
            ...commitment,
            goal: {
                ...existingGoal,
            },
        };

        setCommitment(restoredCommitment);

        setUiMode(UIMode.View);
    }, [
        commitment,
        existingGoal,
    ]);

    const onChangeCommitmentInput = useCallback((value: string) => {
        const parsedValue = parseFloat(value);

        const newCommitment = {
            ...(commitment ?? {
                campaignId,
                channel,
                personId: !useRockApis && !isNil(personId) ? personId : undefined,
            }),
            goal: {
                ...(commitment?.goal ?? { currency }),
                amount: isFinite(parsedValue) ? parsedValue : null,
                amountAsString: value,
            },
        };

        setCommitment(newCommitment);

        setIsDirtyInEditMode(
            uiMode === UIMode.Edit && newCommitment.goal.amount !== existingGoal.amount,
        );
    }, [
        campaignId,
        channel,
        currency,
        existingGoal,
        commitment,
        personId,
        uiMode,
        useRockApis,
    ]);

    const onCloseSnackbar = useCallback(() => {
        setIsSnackbarOpen(false);
    }, []);

    const onEditGoalClicked = useCallback(() => {
        setExistingGoal({ ...commitment.goal });
        setIsDirtyInEditMode(false);
        setUiMode(UIMode.Edit);
    }, [
        commitment,
    ]);

    const onCommitmentSaved = useCallback((newCommitment: CampaignCommitmentModel) => {
        if (isFunction(onCommitmentSavedProp)) {
            onCommitmentSavedProp(newCommitment);
            return;
        }

        if (isString(onCommitmentSavedProp) && !isEmpty(onCommitmentSavedProp)) {
            const callback = eval(onCommitmentSavedProp); // eslint-disable-line no-eval
            callback(newCommitment);
        }
    }, [onCommitmentSavedProp]);

    const onSaveCommitment = useCallback(() => {
        if (!isSaving) {
            setIsSaving(true);

            const saveApiCall = uiMode === UIMode.Add ?
                postCampaignCommitment :
                putCampaignCommitment;

            setSaveMessage(uiMode === UIMode.Add ? t('common.userMessages.commitmentSaved') : t('common.userMessages.commitmentUpdated'));

            saveApiCall(
                accessToken,
                commitment,
                formattingLocale,
                safeCampusId,
            )
                .then((savedCommitment) => {
                    setIsSaveSuccessful(true);
                    setIsSnackbarOpen(true);
                    onCommitmentSaved(savedCommitment);

                    const editableCommitment = {
                        ...savedCommitment,
                        goal: {
                            ...savedCommitment.goal,
                            amountAsString: commitment.goal.amountAsString,
                        },
                    };

                    setCommitment(editableCommitment);
                    setUiMode(UIMode.View);
                    setIsDirtyInEditMode(false);
                    setExistingGoal(null);
                })
                .catch(() => {
                    setSaveMessage(t('common.userMessages.commitmentSaveError'));
                    setIsSaveSuccessful(false);
                    setIsSnackbarOpen(true);
                })
                .finally(() => {
                    setIsSaving(false);
                });
        }
    }, [
        accessToken,
        commitment,
        formattingLocale,
        isSaving,
        onCommitmentSaved,
        safeCampusId,
        t,
        uiMode,
    ]);

    const onGiveNowClick = useCallback(() => {
        if (isFunction(onGiveNowClickProp)) {
            onGiveNowClickProp();
            return;
        }

        if (isString(onGiveNowClickProp) && !isEmpty(onGiveNowClickProp)) {
            const callback = eval(onGiveNowClickProp); // eslint-disable-line no-eval
            callback();
        }
    }, [onGiveNowClickProp]);

    const onGiveViaCryptoClick = useCallback(() => {
        if (isFunction(onGiveViaCryptoClickProp)) {
            onGiveViaCryptoClickProp();
            return;
        }

        if (isString(onGiveViaCryptoClickProp) && !isEmpty(onGiveViaCryptoClickProp)) {
            const callback = eval(onGiveViaCryptoClickProp); // eslint-disable-line no-eval
            callback();
            return;
        }

        // eslint-disable-next-line no-console
        console.warn('Giving via Crypto button click is NOT handled');
    }, [onGiveViaCryptoClickProp]);

    const onGiveViaStockClick = useCallback(() => {
        if (isFunction(onGiveViaStocksClickProp)) {
            onGiveViaStocksClickProp();
            return;
        }

        if (isString(onGiveViaStocksClickProp) && !isEmpty(onGiveViaStocksClickProp)) {
            const callback = eval(onGiveViaStocksClickProp); // eslint-disable-line no-eval
            callback();
            return;
        }

        // eslint-disable-next-line no-console
        console.warn('Giving via Stocks button click is NOT handled');
    }, [onGiveViaStocksClickProp]);

    const onSignInClick = useCallback(() => {
        if (isFunction(onSignInClickProp)) {
            onSignInClickProp();
            return;
        }

        if (isString(onSignInClickProp) && !isEmpty(onSignInClickProp)) {
            const callback = eval(onSignInClickProp); // eslint-disable-line no-eval
            callback();
        }
    }, [onSignInClickProp]);

    if (isNil(campaignId)) {
        return null;
    }

    const isFetching: boolean = isFetchingCampaign || isFetchingCommitment || isSaving;

    const rootClassNames = ClassNames(
        classes.root,
        BEM_BLOCK_NAME,
    );

    const loadingIndicatorClassNames = ClassNames(
        classes.loadingIndicatorContainer,
        CLASS_NAME_LOADING_INDICATOR_CONTAINER,
    );

    const signInMessageContainerClassNames = ClassNames(
        classes.signInMessageContainer,
        CLASS_NAME_SIGN_IN_MESSAGE_CONTAINER,
    );

    const signInMessageClassNames = ClassNames(classes.signInMessage, CLASS_NAME_SIGN_IN_MESSAGE);

    const notFetchingAndNoError: boolean = !isFetching && !isSaving && !hasError;
    const shouldShowSignInMessage: boolean = !isUserLoggedIn && uiMode !== UIMode.View;

    return (
        <div className={rootClassNames}>
            <h1>{t('common.infoContainer.campaignName')}</h1>

            {(isFetching) && (
                <div
                    className={loadingIndicatorClassNames}
                    data-testid={CLASS_NAME_LOADING_INDICATOR_CONTAINER}
                >
                    <ActivityIndicator />
                </div>
            )}

            {hasError && (
                <LoadError subtitle={t('common.infoContainer.loadError')} />
            )}

            {shouldShowSignInMessage && (
                <div
                    className={signInMessageContainerClassNames}
                    data-testid={`${BEM_BLOCK_NAME}--sign_in_message_container`}
                >
                    <Grid.Column sm={12}>
                        <CommitmentTitle textKey="common.infoContainer.reviewTitle" />
                    </Grid.Column>

                    <Grid.Column sm={12}>
                        <Typography
                            className={signInMessageClassNames}
                            data-testid={`${BEM_BLOCK_NAME}--sign_in_message`}
                            variant="body2"
                        >
                            {t('common.infoContainer.signedOutInfoText')}
                        </Typography>

                        <Button
                            className={ClassNames(classes.signInButton, CLASS_NAME_SIGN_IN_BUTTON)}
                            fluid
                            id={CLASS_NAME_SIGN_IN_BUTTON}
                            onClick={onSignInClick}
                        >
                            {t('common.infoContainer.signInButton')}
                        </Button>
                    </Grid.Column>
                </div>
            )}

            {notFetchingAndNoError && uiMode === UIMode.View && (
                <ViewCommitment
                    campusId={safeCampusId}
                    commitment={commitment}
                    locale={formattingLocale}
                    onEditGoalClicked={onEditGoalClicked}
                />
            )}

            {isUserLoggedIn && notFetchingAndNoError && uiMode !== UIMode.View && (
                <SetCommitment
                    campaignName={campaign?.name ?? ''}
                    currency={currency}
                    goal={commitment?.goal ?? null}
                    isDirtyInEditMode={isDirtyInEditMode}
                    locale={formattingLocale}
                    mode={uiMode}
                    onCancelEditingGoal={onCancelEditMode}
                    onChangeGoalAmount={onChangeCommitmentInput}
                    onSaveCommitment={onSaveCommitment}
                />
            )}

            {notFetchingAndNoError && (
                <div
                    className={ClassNames(classes.giveNowContainer, CLASS_NAME_GIVE_NOW_CONTAINER)}
                >
                    {!suppressGiveNowTitleAndBlurb && (
                        <React.Fragment>
                            {!hideWaysToGiveTitle && (
                                <Typography
                                    // eslint-disable-next-line max-len
                                    className={ClassNames(classes.giveNowTitle, CLASS_NAME_GIVE_NOW_TITLE)}
                                    component="div"
                                    variant="body1"
                                >
                                    {t('common.waysToGive.title')}
                                </Typography>
                            )}

                            <Typography
                                // eslint-disable-next-line max-len
                                className={ClassNames(classes.giveNowCopy, CLASS_NAME_GIVE_NOW_COPY)}
                                component="div"
                                variant="body1"
                            >
                                {!isNil(commitment) ? (
                                    <React.Fragment>
                                        {t('common.waysToGive.commitmentMadeSubTitle')}
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        {t('common.waysToGive.noCommitmentSubTitle')}
                                    </React.Fragment>
                                )}
                            </Typography>
                        </React.Fragment>
                    )}

                    {hasSpecialGivingInstructions ? (
                        <InternationalGivingInstructions
                            campaignName={localizedCampaignName}
                            campusId={safeCampusId}
                            locale={formattingLocale}
                        />

                    ) : (
                        <div
                            className={ClassNames(
                                classes.buttonContainer,
                                CLASS_NAME_BUTTON_CONTAINER,
                            )}
                        >
                            <Button
                                // eslint-disable-next-line max-len
                                className={ClassNames(classes.giveNowButton, CLASS_NAME_GIVE_NOW_BUTTON)}
                                fluid
                                id={CLASS_NAME_GIVE_NOW_BUTTON}
                                onClick={onGiveNowClick}
                                outlined
                            >
                                {/* giveNowButtonText */}
                                {/* TODD/FIXME: How should customized text via props get reconciled with translations??!! */}
                                {t('common.waysToGive.giveNowButtonText')}
                            </Button>

                            <Button
                                // eslint-disable-next-line max-len
                                className={ClassNames(classes.cryptoButton, CLASS_NAME_GIVE_WITH_CRYPTO_BUTTON)}
                                fluid
                                id={CLASS_NAME_GIVE_WITH_CRYPTO_BUTTON}
                                onClick={onGiveViaCryptoClick}
                                outlined
                            >
                                {/* giveCryptoButtonText */}
                                {/* TODD/FIXME: How should customized text via props get reconciled with translations??!! */}
                                {t('common.waysToGive.giveCryptoButtonText')}
                            </Button>

                            <Button
                                // eslint-disable-next-line max-len
                                className={ClassNames(classes.stocksButton, CLASS_NAME_GIVE_WITH_STOCKS_BUTTON)}
                                fluid
                                id={CLASS_NAME_GIVE_WITH_STOCKS_BUTTON}
                                onClick={onGiveViaStockClick}
                                outlined
                            >
                                {/* giveStockButtonText */}
                                {/* TODD/FIXME: How should customized text via props get reconciled with translations??!! */}
                                {t('common.waysToGive.giveStockButtonText')}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                autoHideDuration={3000}
                message={(
                    <React.Fragment>
                        {isSaveSuccessful ? (
                            <Icon
                                color="success"
                                inverse
                                style={{
                                    marginRight: 5,
                                }}
                                type="check"
                            />
                        ) : (
                            <Icon
                                color="alert"
                                inverse
                                style={{
                                    marginRight: 5,
                                }}
                                type="close"
                            />
                        )}

                        {saveMessage}
                    </React.Fragment>
                )}
                onClose={onCloseSnackbar}
                open={isSnackbarOpen}
            />
        </div>
    );
}

CampaignCommitment.defaultProps = defaultProps;

export default CampaignCommitment;
